import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import { Plus, Loader2, X, ShieldCheck, Clock, RefreshCw, ArrowRight, Wallet, Trash2 } from "lucide-react";
import { ackText } from "../lib/advisorTerms";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  expired: "bg-neutral-200 text-neutral-600",
  revoked: "bg-rose-100 text-rose-700",
};
const STATUS_LABELS = {
  pending: "Awaiting client confirmation",
  confirmed: "Client Confirmed",
  expired: "Expired",
  revoked: "Revoked",
};

function AckModal({ clientName, busy, onConfirm, onClose, title }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ack-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-neutral-900">{title}</h3>
          <button onClick={onClose} data-testid="ack-modal-close" className="rounded p-1 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-800">
          <input type="checkbox" data-testid="ack-modal-checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#0A8B85]" />
          <span>{ackText(clientName)}</span>
        </label>
        <button type="button" data-testid="ack-modal-confirm" disabled={!checked || busy} onClick={onConfirm}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm and send request"}
        </button>
      </div>
    </div>
  );
}

export default function AdvisorDashboard() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [clients, setClients] = useState(null);
  const [overview, setOverview] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "" });
  const [ackFor, setAckFor] = useState(null); // {mode:'add'|'resend', client}
  const [busy, setBusy] = useState(false);
  const [entering, setEntering] = useState(null);
  const [removeFor, setRemoveFor] = useState(null);
  const [removing, setRemoving] = useState(false);

  const removeClient = async () => {
    if (!removeFor) return;
    setRemoving(true);
    try {
      await api.delete(`/advisors/me/clients/${removeFor.link_id}`);
      setClients((cs) => (cs || []).filter((c) => c.link_id !== removeFor.link_id));
      toast.success(`${removeFor.client_name} removed from your client list`);
      setRemoveFor(null);
      api.get("/advisors/me/overview").then((r) => setOverview(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove this client");
    } finally { setRemoving(false); }
  };

  const load = () => {
    api.get("/advisors/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/advisors/me/clients").then((r) => setClients(r.data?.clients || [])).catch(() => setClients([]));
    api.get("/advisors/me/overview").then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const enterClient = async (card) => {
    setEntering(card.link_id);
    try {
      const { data } = await api.post(`/advisors/me/clients/${card.link_id}/enter`);
      if (data?.token) setAuthToken(data.token);
      await refresh();
      nav("/founder/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open this client account");
      setEntering(null);
    }
  };

  const doAck = async () => {
    setBusy(true);
    try {
      if (ackFor.mode === "add") {
        await api.post("/advisors/me/clients", { ...ackFor.client, acknowledged: true });
        toast.success("Request sent", { description: `${ackFor.client.name} will receive a confirmation email.` });
        setAddOpen(false); setNewClient({ name: "", email: "" });
      } else {
        await api.post(`/advisors/me/clients/${ackFor.client.link_id}/resend`, { acknowledged: true });
        toast.success("Request re-sent", { description: `${ackFor.client.client_name} has a fresh 7-day confirmation link.` });
      }
      setAckFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the request");
    } finally { setBusy(false); }
  };

  const status = me?.advisor_status || "pending";
  const banner = {
    pending: { cls: "border-amber-300 bg-amber-50 text-amber-900", icon: Clock,
      text: "Your advisor account is pending Clinq approval. Once approved, you'll have access to clients who have confirmed your request." },
    approved: { cls: "border-emerald-300 bg-emerald-50 text-emerald-900", icon: ShieldCheck,
      text: "Your advisor account is approved. You have access to clients who have confirmed your request." },
    deactivated: { cls: "border-rose-300 bg-rose-50 text-rose-900", icon: X,
      text: "Your advisor account has been deactivated. Contact support@clinq.africa if you believe this is an error." },
  }[status];

  return (
    <AppShell nav={[]}>
      <div className="mx-auto w-full max-w-4xl pb-32" data-testid="advisor-dashboard">
        <h1 className="text-2xl font-bold tracking-tight text-neutral-900">Advisor dashboard</h1>
        {me && (
          <p className="mt-1 text-sm text-neutral-600">{me.name} · {me.email}</p>
        )}

        {me && banner && (
          <div className={`mt-5 flex items-start gap-3 rounded-xl border p-4 text-sm ${banner.cls}`} data-testid="advisor-status-banner">
            <banner.icon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{banner.text}</span>
          </div>
        )}

        {/* Approved-client cards — click through to manage the account */}
        {status === "approved" && overview && overview.cards.length > 0 && (
          <div className="mt-8">
            <h2 className="text-base font-bold text-neutral-900">Manage a client</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {overview.cards.map((c, i) => (
                <button key={c.link_id} type="button" onClick={() => enterClient(c)}
                  data-testid={`advisor-manage-card-${i}`}
                  className="group rounded-2xl border border-neutral-200 bg-white p-5 text-left transition-all hover:border-[#0A8B85] hover:shadow-md">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-neutral-900">{c.client_name}</p>
                      <p className="truncate text-xs text-neutral-500">{c.client_email}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]">
                      Stage {c.stage}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
                    <span>{c.score_kind}: <b className="text-neutral-900">{c.score ?? "N/A"}</b></span>
                    <span className="capitalize">Income: <b className="text-neutral-900">{c.income_bracket}</b></span>
                    {c.open_tickets > 0 && (
                      <span className="font-bold text-amber-600">{c.open_tickets} open ticket{c.open_tickets > 1 ? "s" : ""}</span>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-neutral-400">{c.stage_label}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#0A8B85]">
                    {entering === c.link_id ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…</> : <>Manage client <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fees summary — reference only, invoiced offline */}
        {overview && (
          <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-5" data-testid="advisor-fees-summary">
            <h2 className="flex items-center gap-2 text-base font-bold text-neutral-900">
              <Wallet className="h-4 w-4 text-[#0A8B85]" /> Fees summary
            </h2>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Clients managed</p>
                <p className="mt-0.5 text-xl font-extrabold text-neutral-900" data-testid="fees-clients-managed">{overview.fees.clients_managed}</p>
              </div>
              {[["Platform fees paid", overview.fees.platform_fees, "fees-platform"],
                ["Success fees paid", overview.fees.success_fees, "fees-success"],
                ["Your 10% entitlement", overview.fees.entitlement, "fees-entitlement"]].map(([label, obj, tid]) => (
                <div key={tid}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{label}</p>
                  <p className="mt-0.5 text-xl font-extrabold text-neutral-900" data-testid={tid}>
                    {Object.keys(obj || {}).length === 0 ? "0"
                      : Object.entries(obj).map(([cur, v]) => `${cur.toUpperCase()} ${Number(v).toLocaleString()}`).join(" · ")}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-neutral-400">
              For reference only — payment happens offline. Invoice Clinq Africa for fees earned; payment terms are 30 days from invoice receipt.
            </p>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <h2 className="text-base font-bold text-neutral-900">My clients</h2>
          {status !== "deactivated" && (
            <button type="button" data-testid="advisor-add-client-btn" onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#066667]">
              <Plus className="h-3.5 w-3.5" /> Add client
            </button>
          )}
        </div>

        <div className="mt-4 space-y-2">
          {clients === null ? (
            <p className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          ) : clients.length === 0 ? (
            <p className="rounded-xl border border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500" data-testid="advisor-no-clients">
              No clients yet — add your first founder client above.
            </p>
          ) : clients.map((c, i) => (
            <div key={c.link_id}
              onClick={c.status === "confirmed" ? () => enterClient(c) : undefined}
              role={c.status === "confirmed" ? "button" : undefined}
              className={"flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white p-4" +
                (c.status === "confirmed" ? " cursor-pointer transition-all hover:border-[#0A8B85] hover:shadow-md" : "")}
              data-testid={`advisor-client-${i}`}>
              <div className="min-w-0">
                <p className="text-sm font-bold text-neutral-900">{c.client_name}</p>
                <p className="text-xs text-neutral-500">{c.client_email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={"rounded-full px-2.5 py-1 text-[11px] font-bold " + (STATUS_STYLES[c.status] || "")}
                  data-testid={`client-status-${i}`}>
                  {STATUS_LABELS[c.status] || c.status}
                </span>
                {c.status === "confirmed" && status === "approved" && (
                  <button type="button" data-testid={`client-manage-${i}`}
                    disabled={entering === c.link_id}
                    onClick={(e) => { e.stopPropagation(); enterClient(c); }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-[11px] font-bold text-white hover:bg-[#066667] disabled:opacity-60">
                    {entering === c.link_id
                      ? <><Loader2 className="h-3 w-3 animate-spin" /> Opening…</>
                      : <>Manage Client <ArrowRight className="h-3 w-3" /></>}
                  </button>
                )}
                {c.status === "expired" && status !== "deactivated" && (
                  <button type="button" data-testid={`client-resend-${i}`}
                    onClick={(e) => { e.stopPropagation(); setAckFor({ mode: "resend", client: c }); }}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-2.5 py-1 text-[11px] font-bold text-neutral-600 hover:border-[#0A8B85] hover:text-[#0A8B85]">
                    <RefreshCw className="h-3 w-3" /> Re-send request
                  </button>
                )}
                <button type="button" data-testid={`client-remove-${i}`} title="Remove client from my list"
                  onClick={(e) => { e.stopPropagation(); setRemoveFor(c); }}
                  className="rounded-lg border border-transparent p-1.5 text-neutral-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {removeFor && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" data-testid="remove-client-modal">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <h3 className="text-base font-bold text-neutral-900">Remove {removeFor.client_name}?</h3>
              <p className="mt-2 text-sm text-neutral-600">
                <b>{removeFor.client_name}</b> ({removeFor.client_email}) will be removed from your client list and you will lose
                access to their profile. Their Clinq account and data are not affected. You can add them again later — they will
                need to confirm your request once more.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setRemoveFor(null)} disabled={removing} data-testid="remove-client-cancel"
                  className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">Cancel</button>
                <button type="button" onClick={removeClient} disabled={removing} data-testid="remove-client-confirm"
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                  {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remove client
                </button>
              </div>
            </div>
          </div>
        )}

        {addOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" data-testid="add-client-modal">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-neutral-900">Add a founder client</h3>
                <button onClick={() => setAddOpen(false)} data-testid="add-client-close" className="rounded p-1 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs font-bold text-neutral-700">Client business name</label>
                  <input data-testid="new-client-name" value={newClient.name} onChange={(e) => setNewClient((c) => ({ ...c, name: e.target.value }))}
                    className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]" placeholder="Acme Ltd" />
                </div>
                <div>
                  <label className="text-xs font-bold text-neutral-700">Founder email</label>
                  <input data-testid="new-client-email" type="email" value={newClient.email} onChange={(e) => setNewClient((c) => ({ ...c, email: e.target.value }))}
                    className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]" placeholder="founder@acme.co.ke" />
                </div>
                <button type="button" data-testid="new-client-continue"
                  disabled={!newClient.name.trim() || !/\S+@\S+\.\S+/.test(newClient.email)}
                  onClick={() => setAckFor({ mode: "add", client: { ...newClient } })}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-40">
                  Continue to acknowledgement
                </button>
              </div>
            </div>
          </div>
        )}

        {ackFor && (
          <AckModal
            clientName={ackFor.mode === "add" ? ackFor.client.name : ackFor.client.client_name}
            busy={busy}
            title="Confidentiality acknowledgement"
            onConfirm={doAck}
            onClose={() => setAckFor(null)}
          />
        )}
      </div>
    </AppShell>
  );
}
